// Admin Tenants Tab Component
import React, { useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit2, Key, Clock, Crown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAdmin, API_URL } from './AdminContext';

export function AdminTenantsTab() {
  const { tenants, plans, fetchTenants } = useAdmin();
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [tenantAction, setTenantAction] = useState(null);
  const [formData, setFormData] = useState({});

  const openTenantDialog = (tenant, action) => {
    setSelectedTenant(tenant);
    setTenantAction(action);
    setFormData({
      name: tenant.name || '',
      newPassword: '',
      extendDays: 30,
      newPlanId: tenant.plan_id || ''
    });
    setShowTenantDialog(true);
  };

  const handleTenantAction = async () => {
    if (!selectedTenant) return;

    try {
      let response;
      switch (tenantAction) {
        case 'edit':
          response = await fetch(`${API_URL}/api/tenants/${selectedTenant.tenant_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: formData.name })
          });
          break;
        case 'resetPassword':
          response = await fetch(`${API_URL}/api/tenants/${selectedTenant.tenant_id}/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ new_password: formData.newPassword })
          });
          break;
        case 'extendTrial':
          response = await fetch(`${API_URL}/api/tenants/${selectedTenant.tenant_id}/extend-trial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ days: parseInt(formData.extendDays) || 30 })
          });
          break;
        case 'changePlan':
          response = await fetch(`${API_URL}/api/tenants/${selectedTenant.tenant_id}/change-plan`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ plan_id: formData.newPlanId })
          });
          break;
        case 'delete':
          if (!confirm(`Are you sure you want to delete tenant "${selectedTenant.name}"? This action cannot be undone.`)) {
            return;
          }
          response = await fetch(`${API_URL}/api/tenants/${selectedTenant.tenant_id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          break;
        default:
          return;
      }

      if (response?.ok) {
        toast.success(`Tenant ${tenantAction === 'delete' ? 'deleted' : 'updated'} successfully`);
        setShowTenantDialog(false);
        fetchTenants();
      } else {
        const error = await response?.json();
        toast.error(error?.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const getDialogContent = () => {
    switch (tenantAction) {
      case 'edit':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Edit Tenant</DialogTitle>
              <DialogDescription>Update tenant information</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Tenant Name</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
          </>
        );
      case 'resetPassword':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>Set a new password for {selectedTenant?.name}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>New Password</Label>
              <Input 
                type="password"
                value={formData.newPassword} 
                onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                placeholder="Enter new password"
              />
            </div>
          </>
        );
      case 'extendTrial':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Extend Trial</DialogTitle>
              <DialogDescription>Extend trial period for {selectedTenant?.name}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Days to Extend</Label>
              <Input 
                type="number"
                value={formData.extendDays} 
                onChange={(e) => setFormData({...formData, extendDays: e.target.value})}
              />
            </div>
          </>
        );
      case 'changePlan':
        return (
          <>
            <DialogHeader>
              <DialogTitle>Change Plan</DialogTitle>
              <DialogDescription>Change subscription plan for {selectedTenant?.name}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>New Plan</Label>
              <Select value={formData.newPlanId} onValueChange={(v) => setFormData({...formData, newPlanId: v})}>
                <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.display_name} - ₹{plan.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'delete':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="text-red-500">Delete Tenant</DialogTitle>
              <DialogDescription>
                This will permanently delete {selectedTenant?.name} and all associated data. 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <GroupBox title={`Tenants (${tenants.length})`} className="border-yellow-500/50">
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3">Tenant</th>
                <th className="text-left py-2 px-3">Plan</th>
                <th className="text-center py-2 px-3">Subscription</th>
                <th className="text-center py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Created</th>
                <th className="text-center py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(tenant => (
                <tr key={tenant.tenant_id} className="border-b border-gray-800">
                  <td className="py-2 px-3">
                    <div>{tenant.name}</div>
                    <div className="text-xs text-gray-500">{tenant.email || tenant.tenant_id}</div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      tenant.plan === 'free' ? 'bg-gray-700 text-gray-300' : 'bg-yellow-900/30 text-yellow-400'
                    }`}>
                      {tenant.plan || 'Free'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      tenant.subscription_status === 'active' ? 'bg-green-900/30 text-green-400' : 
                      tenant.subscription_status === 'expired' ? 'bg-red-900/30 text-red-400' : 
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {tenant.subscription_status || 'None'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      tenant.pending_deletion ? 'bg-red-500/20 text-red-500' :
                      tenant.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'
                    }`}>
                      {tenant.pending_deletion ? 'Deleting' : tenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-400">
                    {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="sm" title="Edit Tenant" onClick={() => openTenantDialog(tenant, 'edit')}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Reset Password" onClick={() => openTenantDialog(tenant, 'resetPassword')}>
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Extend Trial" onClick={() => openTenantDialog(tenant, 'extendTrial')}>
                        <Clock className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Change Plan" onClick={() => openTenantDialog(tenant, 'changePlan')}>
                        <Crown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400" title="Delete Tenant" onClick={() => openTenantDialog(tenant, 'delete')}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GroupBox>

      {/* Tenant Action Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent>
          {getDialogContent()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantDialog(false)}>Cancel</Button>
            <Button 
              variant={tenantAction === 'delete' ? 'destructive' : 'default'}
              onClick={handleTenantAction}
            >
              {tenantAction === 'delete' ? 'Delete' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
